// The shell's half of the topology's focus view (#1501, after #1442). Entering
// it slides the left sidebar (`.nav`) out to the left and the scope bar
// (`.rail`) up, while the topology panel grows from its place to the whole tab;
// leaving slides them back as the panel returns. On a phone, where the sidebar
// is the top bar, the top bar slides up.
//
// **The state is the focus view's alone.** It lives in React state on the
// shell's `.app` element and nowhere else: no address, no storage. The page
// that opened the view publishes it, and its unmount publishes the page again,
// so navigating away or reloading shows the shell as it always is.
//
// The sidebar and the scope bar move by `transform` only, so the page under
// them keeps its layout and its scroll position while the view is open. The
// motion is one ease-out (`--focus-slide`, `--focus-ease` in theme.css), and
// it is declared only under `prefers-reduced-motion: no-preference`; a browser
// that asks for reduced motion, or cannot say, gets the change at once.
import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from "react";

/** Where the focus view is: the page, moving into the view, in it, or moving
 * back out. */
export type FocusPhase = "page" | "entering" | "focus" | "leaving";

/** How long the slide takes, in milliseconds. `theme.css`'s `--focus-slide`
 * is the same value; this one bounds the wait for the panel's own
 * `transitionend`, which a hidden tab may never send. */
export const SLIDE_MS = 220;

/** The query under which the focus view moves at all. */
export const MOTION = "(prefers-reduced-motion: no-preference)";

/** Whether this browser takes the slide: it said it has no preference for
 * reduced motion. A browser without `matchMedia` cannot say, and gets none. */
export function moves(): boolean {
  return window.matchMedia?.(MOTION).matches === true;
}

/** The classes the shell's `.app` carries in each phase: `is-focus` while the
 * sidebar and the scope bar are away or going, `is-sliding` while they move. */
export function focusClasses(phase: FocusPhase): string[] {
  switch (phase) {
    case "entering": return ["is-focus", "is-sliding"];
    case "focus": return ["is-focus"];
    case "leaving": return ["is-sliding"];
    default: return [];
  }
}

interface FocusShell {
  /** The focus view says where it is. */
  publish(phase: FocusPhase): void;
  /** The top of the area the shell leaves to the page: the scope bar's foot
   * where it is in view, else the top of the window. */
  top(): number;
}

const Ctx = createContext<FocusShell>({ publish: () => {}, top: () => 0 });

/** What the focus view reaches the shell through. Outside a shell — a page
 * rendered alone — publishing does nothing and the page's area starts at the
 * top of the window. */
export function useFocusShell(): FocusShell {
  return useContext(Ctx);
}

/** The shell's `.app` element, carrying the focus view's state. */
export function FocusFrame({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<FocusPhase>("page");
  const frame = useRef<HTMLDivElement>(null);
  const value = useMemo((): FocusShell => ({
    publish: setPhase,
    top: () => Math.max(0, frame.current?.querySelector(":scope > .main > .rail")?.getBoundingClientRect().bottom ?? 0),
  }), []);
  return (
    <Ctx.Provider value={value}>
      <div ref={frame} className={["app", ...focusClasses(phase)].join(" ")}>{children}</div>
    </Ctx.Provider>
  );
}
